import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import {
  activeAppsPageQuery,
  activeAppsTableQuery,
  closeRunningApplication,
  type ActiveAppRow,
} from '../api/devices'
import { toast } from '../ui/notify'
import { LENGTHS, pageButtons } from '../ui/DataTable'
import { SimpleLineChart } from '../ui/SimpleLineChart'
import { Spinner } from '../ui/Spinner'
import { convertBytes } from './convertBytes'
import type { ScreenProps } from './screens'

/**
 * Fila Active Applications - opteemum/devices/details/runningApplications.php:
 * graficul cu numarul (amChartSimpleLine, #36a3d9), tabelul
 * RunningApplicationsModel, cautarea din sessionStorage odNmAppFilter,
 * sortarea implicita [[2, 'desc']] pe CPU. Close posteaza application_id
 * si device ca Opteemum::closeRunningApplication.
 */

const TABLE = 'running-applications'

export function DeviceActiveApps({ id }: ScreenProps) {
  const page = useQuery(activeAppsPageQuery(id))
  const [search, setSearch] = useState('')
  const [start, setStart] = useState(0)
  const [length, setLength] = useState(10)
  const [order, setOrder] = useState<{ column: number; dir: 'asc' | 'desc' }>({ column: 2, dir: 'desc' })
  const seeded = useRef(false)

  useEffect(() => {
    if (seeded.current) return
    seeded.current = true
    try {
      const pending = sessionStorage.getItem('odNmAppFilter') || ''
      sessionStorage.removeItem('odNmAppFilter')
      if (pending) setSearch(pending)
    } catch {
      /* private mode */
    }
  }, [])

  const table = useQuery({
    ...activeAppsTableQuery(id, {
      start,
      length,
      search,
      orderColumn: order.column,
      orderDir: order.dir,
    }),
    placeholderData: keepPreviousData,
    enabled: !!page.data,
  })

  if (page.error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {page.error.message}
      </div>
    )
  }
  if (!page.data) return <Spinner />

  const chart = page.data.chart

  return (
    <div className="device-active-apps">
      <label className="color">
        <strong>Running applications</strong> count
      </label>
      <div className="runningApplicationsChart" style={{ width: '100%', height: 150, marginBottom: 20 }}>
        {chart.length === 0 ? (
          <div className="alert alert-info">
            <strong>
              <i className="fas fa-info-circle"></i>
            </strong>{' '}
            Nothing to see yet...
          </div>
        ) : (
          <SimpleLineChart points={chart} unit="Applications" color="#36a3d9" />
        )}
      </div>
      <div className="row">
        <div className="col-md-12">
          <DetailsTable
            rows={table.data?.data ?? []}
            total={table.data?.recordsFiltered ?? 0}
            fetching={table.isFetching}
            error={table.isError ? (table.error as Error).message : null}
            search={search}
            start={start}
            length={length}
            order={order}
            onSearch={(q) => {
              setSearch(q)
              setStart(0)
            }}
            onStart={setStart}
            onLength={(n) => {
              setLength(n)
              setStart(0)
            }}
            onOrder={(next) => {
              setOrder(next)
              setStart(0)
            }}
            onClose={async (applicationId) => {
              await closeRunningApplication(id, applicationId)
              toast('Close application instruction sent.', 'Success')
              await table.refetch()
            }}
          />
        </div>
      </div>
    </div>
  )
}

const COLUMNS: { key: keyof ActiveAppRow | 'cpu' | 'memory' | 'actions'; title: string; orderable: boolean }[] = [
  { key: 'application_name', title: 'Application', orderable: true },
  { key: 'application_id', title: 'ID', orderable: true },
  { key: 'cpu', title: 'CPU Load', orderable: true },
  { key: 'memory', title: 'Memory Used', orderable: true },
  { key: 'priority', title: 'Priority', orderable: true },
  { key: 'threads', title: 'Threads', orderable: true },
  { key: 'io_per_sec', title: 'IO/sec', orderable: true },
  { key: 'actions', title: 'Actions', orderable: false },
]

function cell(row: ActiveAppRow, key: (typeof COLUMNS)[number]['key']): string {
  if (key === 'cpu') return `${row.cpu_load}%`
  if (key === 'memory') return convertBytes(Number(row.memory_used) || 0)
  if (key === 'actions') return row.closeForm
  return String(row[key] ?? '')
}

function DetailsTable({
  rows,
  total,
  fetching,
  error,
  search,
  start,
  length,
  order,
  onSearch,
  onStart,
  onLength,
  onOrder,
  onClose,
}: {
  rows: ActiveAppRow[]
  total: number
  fetching: boolean
  error: string | null
  search: string
  start: number
  length: number
  order: { column: number; dir: 'asc' | 'desc' }
  onSearch(q: string): void
  onStart(n: number): void
  onLength(n: number): void
  onOrder(next: { column: number; dir: 'asc' | 'desc' }): void
  onClose(applicationId: string): Promise<void>
}) {
  const pages = Math.max(1, Math.ceil(total / length) || 1)
  const page = Math.floor(start / length)
  const from = total === 0 ? 0 : start + 1
  const to = Math.min(start + length, total)
  const [draft, setDraft] = useState(search)
  const typing = useRef<number | null>(null)

  useEffect(() => {
    setDraft(search)
  }, [search])

  useEffect(() => {
    return () => {
      if (typing.current !== null) window.clearTimeout(typing.current)
    }
  }, [])

  return (
    <div className="dataTables_wrapper form-inline dt-bootstrap no-footer" id={`${TABLE}_wrapper`}>
      <div className="row">
        <div className="col-sm-6 dataTableF">
          <div id={`${TABLE}_filter`} className="dataTables_filter input-icon">
            <label>
              Search:{' '}
              <input
                type="search"
                className="form-control input-sm"
                aria-controls={TABLE}
                value={draft}
                onChange={(e) => {
                  const value = e.target.value
                  setDraft(value)
                  if (typing.current !== null) window.clearTimeout(typing.current)
                  typing.current = window.setTimeout(() => onSearch(value), 400)
                }}
              />
            </label>
          </div>
        </div>
        <div className="col-sm-6 dataTableA"></div>
      </div>
      <div className="row">
        <div className="col-sm-12 userFilters"></div>
      </div>
      <div className="row">
        <div className="col-sm-12">
          <div className="dataTables_processing" id={`${TABLE}_processing`} style={{ display: fetching ? 'block' : 'none' }}>
            Processing...
          </div>
          <table
            className="running-applications table table-striped table-hover dataTable no-footer"
            width="100%"
            id={TABLE}
            role="grid"
            onSubmit={(e) => e.preventDefault()}
          >
            <thead>
              <tr role="row">
                {COLUMNS.map((col, i) => {
                  const sorted = order.column === i
                  const cls = [
                    'td-small a-left',
                    col.orderable ? (sorted ? (order.dir === 'asc' ? 'sorting_asc' : 'sorting_desc') : 'sorting') : 'sorting_disabled',
                  ].join(' ')
                  return (
                    <th
                      key={col.key}
                      className={cls}
                      aria-label={`${col.title}: activate to sort column ${sorted && order.dir === 'asc' ? 'descending' : 'ascending'}`}
                      aria-sort={sorted ? (order.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                      onClick={() => {
                        if (!col.orderable) return
                        onOrder({ column: i, dir: sorted && order.dir === 'desc' ? 'asc' : 'desc' })
                      }}
                    >
                      {col.title}
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {error ? (
                <tr className="odd">
                  <td colSpan={8} className="dataTables_empty">
                    Something went wrong: {error}
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr className="odd">
                  <td colSpan={8} className="dataTables_empty">
                    No data available in table
                  </td>
                </tr>
              ) : (
                rows.map((row, i) => (
                  <tr key={row.DT_RowId} id={row.DT_RowId} role="row" className={i % 2 === 0 ? 'odd' : 'even'}>
                    {COLUMNS.map((col) =>
                      col.key === 'actions' ? (
                        <td
                          key={col.key}
                          className="td-small a-left"
                          dangerouslySetInnerHTML={{ __html: row.closeForm }}
                          onSubmit={(e) => {
                            e.preventDefault()
                            const form = e.target as HTMLFormElement
                            const app = (form.elements.namedItem('application_id') as HTMLInputElement | null)?.value
                            if (app) void onClose(app)
                          }}
                        />
                      ) : (
                        <td key={col.key} className="td-small a-left">
                          {cell(row, col.key)}
                        </td>
                      ),
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="row">
        <div className="col-sm-2">
          <div className="dataTables_length custom_length_class" id={`${TABLE}_length`}>
            <label>
              Show{' '}
              <select
                name={`${TABLE}_length`}
                aria-controls={TABLE}
                className="form-control input-sm"
                value={length}
                onChange={(e) => onLength(Number(e.target.value))}
              >
                {LENGTHS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>{' '}
              entries
            </label>
          </div>
        </div>
        <div className="col-sm-4 aright">
          <div className="dataTables_info" id={`${TABLE}_info`} role="status" aria-live="polite">
            Showing {from} to {to} of {total} entries
          </div>
        </div>
        <div className="col-sm-6">
          <div className="dataTables_paginate paging_simple_numbers" id={`${TABLE}_paginate`}>
            <ul className="pagination">
              <li className={`paginate_button previous${page <= 0 ? ' disabled' : ''}`} id={`${TABLE}_previous`}>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault()
                    if (page > 0) onStart((page - 1) * length)
                  }}
                >
                  Previous
                </a>
              </li>
              {pageButtons(page, pages).map((b, i) =>
                b === 'ellipsis' ? (
                  <li key={`e${i}`} className="paginate_button disabled">
                    <a href="#" onClick={(e) => e.preventDefault()}>
                      …
                    </a>
                  </li>
                ) : (
                  <li key={b} className={`paginate_button${b === page ? ' active' : ''}`}>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault()
                        onStart(b * length)
                      }}
                    >
                      {b + 1}
                    </a>
                  </li>
                ),
              )}
              <li className={`paginate_button next${page >= pages - 1 ? ' disabled' : ''}`} id={`${TABLE}_next`}>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault()
                    if (page < pages - 1) onStart((page + 1) * length)
                  }}
                >
                  Next
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="clear"></div>
    </div>
  )
}
